import { BaseService } from '../../core/BaseService';
import { DeleteData, PaginationParams, Wrapper } from '../../type';
import {
  BinRequest,
  BinResponse,
  GetBinsResponse,
  GetRackByIdResponse,
  GetRacksByStoreResponse,
  GetShelvesResponse,
  RackResponse,
  RackShelfBinPayload,
  RackUpdateRequest,
  ShelfRequest,
  ShelfResponse,
} from './interface';

export class RackProfile extends BaseService {
  async getRacks(params: PaginationParams & { storeId: number }) {
    return this.get<GetRacksByStoreResponse>(`${this.apiUrl}/api/racks/paged`, params);
  }

  async getRackById(id: number) {
    return this.get<GetRackByIdResponse>(`${this.apiUrl}/api/racks/${id}`);
  }

  async getShelves(params: PaginationParams & { rackId: number }) {
    return this.get<GetShelvesResponse>(`${this.apiUrl}/api/shelfs/paged`, params);
  }

  async getBins(params: PaginationParams & { shelfId: number }) {
    return this.get<GetBinsResponse>(`${this.apiUrl}/api/bins/paged`, params);
  }

  async getBinsByStore(storeId: number) {
    return this.get<GetBinsResponse>(`${this.apiUrl}/api/bins/get`, {
      storeId,
      page: 1,
      size: 100,
    });
  }

  async createRackWithStructure(req: RackShelfBinPayload) {
    return this.post<Wrapper, RackShelfBinPayload>(`${this.apiUrl}/api/racks/shelfs/bin`, req);
  }

  async createRack(req: RackUpdateRequest) {
    return this.post<Wrapper, RackUpdateRequest>(`${this.apiUrl}/api/racks`, req);
  }

  async updateRack(id: number, req: RackUpdateRequest) {
    return this.put<Wrapper, RackUpdateRequest>(`${this.apiUrl}/api/racks/${id}`, req);
  }

  async deleteRack(id: number) {
    return this.delete<DeleteData>(`${this.apiUrl}/api/racks/${id}`);
  }

  async createShelf(req: ShelfRequest) {
    return this.post<Wrapper, ShelfRequest>(`${this.apiUrl}/api/shelfs`, req);
  }

  async updateShelf(id: number, req: ShelfRequest) {
    return this.put<Wrapper, ShelfRequest>(`${this.apiUrl}/api/shelfs/${id}`, req);
  }

  async deleteShelf(id: number) {
    return this.delete<DeleteData>(`${this.apiUrl}/api/shelfs/${id}`);
  }

  async createBin(req: BinRequest) {
    return this.post<Wrapper, BinRequest>(`${this.apiUrl}/api/bins`, req);
  }

  async updateBin(id: number, req: BinRequest) {
    return this.put<Wrapper, BinRequest>(`${this.apiUrl}/api/bins/${id}`, req);
  }

  async deleteBin(id: number) {
    return this.delete<DeleteData>(`${this.apiUrl}/api/bins/${id}`);
  }
}

const normalizePagedList = <T>(payload: Wrapper & { data?: unknown }): T[] => {
  const data = payload?.data as { content?: T[] } | T[] | undefined;

  if (Array.isArray(data)) return data;

  if (data && Array.isArray(data.content)) return data.content;

  return [];
};

export const normalizeRacksList = (payload: GetRacksByStoreResponse): RackResponse[] =>
  normalizePagedList<RackResponse>(payload);

const normalizeBin = (bin: Partial<BinResponse>, fallbackShelfId?: number): BinResponse => ({
  id: Number(bin.id) || 0,
  code: String(bin.code || ''),
  title: String(bin.title || ''),
  storeId: Number(bin.storeId) || 0,
  rackId: Number(bin.rackId) || 0,
  shelfId: Number(bin.shelfId ?? fallbackShelfId) || 0,
  locationCode: bin.locationCode != null ? String(bin.locationCode) : undefined,
  stockQuantity: bin.stockQuantity,
  itemName: bin.itemName,
});

const normalizeShelf = (shelf: Partial<ShelfResponse>): ShelfResponse => {
  const rawBins = Array.isArray(shelf.binList)
    ? shelf.binList
    : Array.isArray(shelf.bins)
      ? shelf.bins
      : [];
  const shelfId =
    Number(shelf.id) ||
    Number(rawBins[0]?.shelfId) ||
    0;
  return {
    id: shelfId,
    code: String(shelf.code || ''),
    title: String(shelf.title || ''),
    rackId: Number(shelf.rackId) || 0,
    storeId: shelf.storeId != null ? Number(shelf.storeId) : undefined,
    bins: rawBins.map((bin) => normalizeBin(bin, shelfId)),
  };
};

/** Map GET /api/racks/{id} payload (`shelfList` / `binList`) into UI shelves. */
export const normalizeShelvesFromRack = (payload: GetRackByIdResponse): ShelfResponse[] => {
  const data = payload?.data;
  if (!data) return [];
  const rawShelves = Array.isArray(data.shelfList)
    ? data.shelfList
    : Array.isArray(data.shelves)
      ? data.shelves
      : [];
  return rawShelves.map((shelf) => normalizeShelf(shelf));
};

export const normalizeShelvesList = (payload: GetShelvesResponse): ShelfResponse[] =>
  normalizePagedList<ShelfResponse>(payload);

export const normalizeBinsList = (payload: GetBinsResponse): BinResponse[] =>
  normalizePagedList<BinResponse>(payload);

export const getRackCode = (rack: RackResponse) => rack.rackCode || rack.code || '';

export const getRackTitle = (rack: RackResponse) => rack.rackTitle || rack.title || '';
